import { CommissionType, PartnerAffiliation } from '@/@types/PartnerProgram';
import CommissionInput from '@/components/shared/CommissionInput/CommissionInput';
import CurrencyPriceInput from '@/components/shared/CurrencyPriceInput/CurrencyPriceInput';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { formatCurrencyFromCents } from '@/lib/select-options';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUpdatePartnerCommission } from '../../Affiliate/hooks/useUpdatePartnerCommission';
import Divider from '@/components/shared/Divider/Divider';

interface Props {
  productId: string;
  partner: PartnerAffiliation | null;
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  commission_type: CommissionType;
  commission_value: number;
  commission_currency: string;
}

export default function ConfigurePartnerCommissionModal({
  productId,
  partner,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation(['affiliates', 'common']);
  const { updatePartnerCommission, isUpdating } = useUpdatePartnerCommission(productId);
  const [formattedPrice, setFormattedPrice] = useState('0,00');

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    register,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      commission_type: 'fixed',
      commission_value: 0,
      commission_currency: 'BRL',
    },
  });

  const commissionType = watch('commission_type');
  const commissionCurrency = watch('commission_currency');

  useEffect(() => {
    if (isOpen && partner) {
      const type = partner.commission?.type || 'fixed';
      const val = partner.commission?.value || 0;
      const currency = partner.commission?.currency || 'BRL';

      reset({
        commission_type: type,
        commission_value: val,
        commission_currency: currency,
      });

      if (type === 'fixed') {
        setFormattedPrice(formatCurrencyFromCents(val, currency));
      }
    }
  }, [isOpen, partner, reset]);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueStr = e.target.value.replace(/\D/g, '');
    const centsValue = Number.parseInt(valueStr || '0', 10);
    setValue('commission_value', centsValue);
    setFormattedPrice(formatCurrencyFromCents(centsValue, commissionCurrency));
  };

  const onSubmit = (data: FormValues) => {
    if (!partner?.partner?.id) return;

    updatePartnerCommission(
      {
        partnerId: partner.partner.id,
        data,
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  if (!partner) return null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-md flex flex-col p-0 gap-0">
        <SheetHeader className="p-6 border-b border-border">
          <SheetTitle className="flex gap-2">
            {t('affiliates:configure_commission_title', 'Configurar Comissão do Parceiro')}
          </SheetTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {t(
              'affiliates:configure_commission_description',
              'Defina uma comissão específica para {{name}}. Ela irá sobrepor a comissão padrão do programa.',
              { name: partner.partner.name },
            )}
          </p>
        </SheetHeader>

        <form
          id="partner-commission-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 justify-between overflow-y-auto"
        >
          <section className="flex flex-col gap-6 px-6 py-4">
            <div className="space-y-2">
              <Label>{t('affiliates:commission_type', 'Tipo de Comissão')}</Label>
              <Controller
                name="commission_type"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="flex flex-row space-x-4 mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="percentage" id="c-pct" />
                      <Label htmlFor="c-pct">
                        {t('affiliates:commission_type_percentage', 'Porcentagem (%)')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="fixed" id="c-fixed" />
                      <Label htmlFor="c-fixed">
                        {t('affiliates:commission_type_fixed', 'Valor Fixo (R$)')}
                      </Label>
                    </div>
                  </RadioGroup>
                )}
              />
            </div>

            <Divider />

            <div className="space-y-2">
              {commissionType === 'percentage' ? (
                <CommissionInput
                  label={t('affiliates:partner_commission_label', 'Comissão do parceiro')}
                  helperText={errors.commission_value?.message}
                  integerOnly
                  minValue={1}
                  maxValue={10000}
                  defaultValue={watch('commission_value')}
                  onChangeCommission={(value) =>
                    setValue('commission_value', Number.parseInt(value || '0', 10))
                  }
                  dataCy="input-partner-commission-pct"
                  {...register('commission_value', {
                    required: t('common:required_field'),
                    validate: {
                      min: (v) => v >= 1 || t('affiliates:commission_min'),
                      max: (v) => v <= 10000 || t('affiliates:commission_max'),
                    },
                  })}
                />
              ) : (
                <CurrencyPriceInput
                  formattedPrice={formattedPrice}
                  onPriceChange={handlePriceChange}
                  currency={commissionCurrency}
                  onCurrencyChange={(value) => setValue('commission_currency', value)}
                  currencies={['BRL', 'USD']}
                  label={t('affiliates:partner_commission_label', 'Comissão do parceiro')}
                  placeholder={t('affiliates:value_placeholder', 'Valor')}
                  dataCy="input-partner-commission-fixed"
                />
              )}
              {errors.commission_value && commissionType === 'fixed' && (
                <p className="text-red-500 text-sm mt-1">{errors.commission_value.message}</p>
              )}
            </div>
          </section>

          <div className="flex items-center justify-end p-4 border-t border-border gap-2 max-sm:pb-12">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              data-cy="btn-cancel-commission-config"
            >
              {t('common:cancel', 'Cancelar')}
            </Button>
            <Button
              type="submit"
              disabled={isUpdating}
              data-cy="btn-save-commission-config"
            >
              {isUpdating && <Loader2 className="animate-spin mr-2 h-4 w-4" />}
              {t('common:save', 'Salvar')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
